import {
  CalendarIcon,
  EllipsisVerticalIcon,
  MapPinIcon,
} from '@heroicons/react/24/solid';
import { truncate } from 'lodash-es';

import { ACTIVE_TRIP_STATUSES } from 'src/common/ACTIVE_TRIP_STATUSES';
import {
  DispatchTripRead,
  RunBambiRunReassignedTripRead,
  RunBambiRunTripRead,
} from 'src/common/external/bambi-api/bambiApi';
import { reassignableTripStatuses } from 'src/common/tripStatuses';
import { RunBambiRunCalendarEventType } from 'src/features/dispatch/common/RUN_BAMBI_RUN_ASSIGNMENT_CATEGORIES';
import { TripDetailsPopover } from 'src/features/dispatch/trips/TripDetailsPopover/TripDetailsPopover';

import { EventBadges } from './EventBadges/EventBadges';
import { getTripCalendarTheme } from './getTripCalendarTheme';
import { TripTitle } from './TripTitle';
import { useTripEventOpacityClass } from './useTripEventOpacityClass';

type TripCalendarEventProps = {
  trip: DispatchTripRead | RunBambiRunTripRead | RunBambiRunReassignedTripRead;
  type?: RunBambiRunCalendarEventType;
};

export function TripCalendarEvent({ trip, type }: TripCalendarEventProps) {
  const theme = getTripCalendarTheme(trip, type);

  const opacityClass = useTripEventOpacityClass(trip, type);

  const shouldShowAsMultiLoad =
    'multiload_id' in trip && trip.multiload_id && !trip.id.includes('preview');

  return (
    <>
      <EventBadges trip={trip} />
      <TripDetailsPopover trip={trip} type={type}>
        <div
          className={`flex h-full flex-row overflow-hidden px-1 text-xs ${opacityClass} 
          ${trip.status === 'en route' && 'border-t-4'}
          ${trip.status === 'at pickup' && 'border-l-4'}
          ${trip.status === 'transporting' && 'border-r-4'}
          ${trip.status === 'at dropoff' && 'border-b-4'}
          ${!ACTIVE_TRIP_STATUSES.includes(trip.status) && 'border-l-4'}
          ${reassignableTripStatuses.includes(trip.status) && 'cursor-grab'}
          `}
          style={{
            backgroundColor: theme.bg,
            borderColor: theme.border,
            borderStyle: shouldShowAsMultiLoad ? 'double' : theme.borderStyle,
            backgroundImage:
              shouldShowAsMultiLoad && !trip.id.includes('preview')
                ? `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='${theme.border.replace(
                    '#',
                    '%23'
                  )}' fill-opacity='0.27'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                : '',
            backgroundRepeat: 'repeat',
            backgroundPositionY: '12px',
            backgroundPositionX: '12px',
            backgroundSize: '72px',
          }}
          data-testid="trip-calendar-event-container"
        >
          <div className="flex w-full flex-1 flex-col gap-0.5">
            <div
              className="flex w-full"
              style={{
                color: theme.text,
                backgroundColor: theme.bg,
              }}
            >
              <TripTitle trip={trip} />
            </div>
            <div>
              <div
                className="flex justify-start gap-1 text-xs"
                style={{
                  color: theme.text,
                  backgroundColor: theme.bg,
                }}
                title={trip.pickup_address}
              >
                <div>
                  <MapPinIcon className="h-4 w-4" />
                </div>
                <div>
                  {truncate(trip.pickup_address, {
                    length: 30,
                    omission: '...',
                  })}
                </div>
              </div>
              <span
                style={{
                  color: theme.text,
                  backgroundColor: theme.bg,
                }}
                className="flex h-4 w-4 items-center justify-start"
              >
                <EllipsisVerticalIcon className="h-3 w-3" />
              </span>
              <div
                className="flex justify-start gap-1 text-xs"
                style={{
                  color: theme.text,
                  backgroundColor: theme.bg,
                }}
              >
                <div>
                  {trip.appointment_at ? (
                    <CalendarIcon className="min-w-4 h-4 w-4" />
                  ) : (
                    <MapPinIcon className="min-w-4 h-4 w-4" />
                  )}
                </div>
                <div>
                  {truncate(trip.dropoff_address, {
                    length: 30,
                    omission: '...',
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </TripDetailsPopover>
    </>
  );
}
